import { useEffect, useState, useCallback } from 'react';
import { Card } from '@ftedn/ui';
import { useApi } from '../../hooks/useApi';

interface Application {
  id: string;
  userId: string;
  motivation: string;
  socialLinks: string[];
  status: string;
  createdAt: string;
}

interface IrlEvent {
  id: string;
  title: string;
  location: string;
  startsAt: string;
  capacity: number;
  rsvpCount: number;
}

export function AmbassadorPage() {
  const { get, post } = useApi();
  const [tab, setTab] = useState<'applications' | 'events' | 'kits'>('applications');
  const [apps, setApps] = useState<Application[]>([]);
  const [events, setEvents] = useState<IrlEvent[]>([]);
  const [kitEligible, setKitEligible] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  // Event creation state
  const [newEvent, setNewEvent] = useState({ title: '', location: '', startsAt: '', capacity: 50 });

  const loadApps = useCallback(async () => {
    try { setApps((await get('/ambassadors/applications')).applications || []); } catch (e) { setError(String(e)); }
  }, []);
  const loadEvents = useCallback(async () => {
    try { setEvents((await get('/ambassadors/events')).events || []); } catch (e) { setError(String(e)); }
  }, []);
  const loadKits = useCallback(async () => {
    try {
      const data = await get('/ambassadors/kit-eligible');
      setKitEligible(data.eligible || []);
    } catch (e) { setError(String(e)); }
  }, []);

  useEffect(() => {
    if (tab === 'applications') loadApps();
    else if (tab === 'events') loadEvents();
    else loadKits();
  }, [tab]);

  const reviewApp = async (id: string, approved: boolean) => {
    try {
      await post(`/ambassadors/applications/${id}/review`, { approved });
      setActionMsg(`✅ Application ${approved ? 'approved' : 'rejected'}`);
      loadApps();
    } catch (e) { setActionMsg(`❌ ${String(e)}`); }
  };

  const createNewEvent = async () => {
    if (!newEvent.title || !newEvent.location || !newEvent.startsAt) return;
    try {
      await post('/ambassadors/events', newEvent);
      setActionMsg('✅ Event created');
      setNewEvent({ title: '', location: '', startsAt: '', capacity: 50 });
      loadEvents();
    } catch (e) { setActionMsg(`❌ ${String(e)}`); }
  };

  if (error) return <Card><p style={{ color: 'var(--color-error)' }}>Error: {error}</p></Card>;

  return (
    <div>
      <h1>🏅 Ambassador Program</h1>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {(['applications', 'events', 'kits'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 20px', borderRadius: 20, border: 'none', cursor: 'pointer',
            fontWeight: tab === t ? 700 : 500, fontSize: '0.85rem',
            background: tab === t ? 'var(--color-primary, #18181b)' : 'var(--color-surface-2, #f3f4f6)',
            color: tab === t ? '#fff' : 'var(--color-text, #111)',
          }}>
            {t === 'applications' ? `📋 Applications (${apps.length})` : t === 'events' ? '🎉 Events' : '📦 Kit Eligible'}
          </button>
        ))}
      </div>

      {actionMsg && <Card><p>{actionMsg}</p></Card>}

      {tab === 'applications' && (
        apps.length === 0 ? (
          <Card><p style={{ color: 'var(--color-text-secondary)' }}>No pending applications</p></Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {apps.map(app => (
              <Card key={app.id}>
                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', margin: '0 0 8px' }}>
                  User: {app.userId.slice(0, 12)}… • Applied: {new Date(app.createdAt).toLocaleDateString()}
                </p>
                <p style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>
                  <strong>Motivation:</strong> {app.motivation}
                </p>
                {app.socialLinks?.length > 0 && (
                  <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', margin: '0 0 8px' }}>
                    Links: {app.socialLinks.join(', ')}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => reviewApp(app.id, true)} style={{
                    padding: '8px 16px', borderRadius: 8, border: 'none',
                    background: '#10b981', color: '#fff', fontWeight: 600, cursor: 'pointer',
                  }}>✅ Approve</button>
                  <button onClick={() => reviewApp(app.id, false)} style={{
                    padding: '8px 16px', borderRadius: 8, border: '1.5px solid #ef4444',
                    background: 'transparent', color: '#ef4444', fontWeight: 600, cursor: 'pointer',
                  }}>❌ Reject</button>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {tab === 'events' && (
        <div>
          <Card>
            <h3>Create Event</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <input placeholder="Event title" value={newEvent.title}
                onChange={e => setNewEvent(p => ({ ...p, title: e.target.value }))}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--color-border, #d1d5db)' }} />
              <input placeholder="Location" value={newEvent.location}
                onChange={e => setNewEvent(p => ({ ...p, location: e.target.value }))}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--color-border, #d1d5db)' }} />
              <input type="datetime-local" value={newEvent.startsAt}
                onChange={e => setNewEvent(p => ({ ...p, startsAt: e.target.value }))}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--color-border, #d1d5db)' }} />
              <input type="number" placeholder="Capacity" value={newEvent.capacity}
                onChange={e => setNewEvent(p => ({ ...p, capacity: parseInt(e.target.value) || 50 }))}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--color-border, #d1d5db)', width: 120 }} />
              <button onClick={createNewEvent} disabled={!newEvent.title || !newEvent.startsAt}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: 'none', alignSelf: 'flex-start',
                  background: 'var(--color-primary, #18181b)', color: '#fff', fontWeight: 600, cursor: 'pointer',
                }}>Create Event</button>
            </div>
          </Card>

          <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', marginTop: '1rem' }}>
            {events.map(ev => (
              <Card key={ev.id}>
                <h3 style={{ margin: '0 0 4px' }}>{ev.title}</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', margin: '0 0 4px' }}>
                  📍 {ev.location}
                </p>
                <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', margin: '0 0 8px' }}>
                  📅 {new Date(ev.startsAt).toLocaleDateString()} • 👥 {ev.rsvpCount}/{ev.capacity}
                </p>
                <div style={{
                  height: 6, borderRadius: 3, background: 'var(--color-surface-2, #e5e7eb)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%', borderRadius: 3,
                    width: `${Math.min((ev.rsvpCount / ev.capacity) * 100, 100)}%`,
                    background: ev.rsvpCount >= ev.capacity ? '#ef4444' : '#10b981',
                  }} />
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {tab === 'kits' && (
        <Card>
          <h2>Quarterly Kit Eligible ({kitEligible.length})</h2>
          {kitEligible.length === 0 ? (
            <p style={{ color: 'var(--color-text-secondary)' }}>No eligible ambassadors yet</p>
          ) : (
            <ul>
              {kitEligible.map(uid => (
                <li key={uid} style={{ fontSize: '0.9rem', marginBottom: '4px' }}>
                  {uid.slice(0, 16)}…
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
